import type { ReactNode } from 'react'
import type { LiveSpec } from '@/lib/market/types'

import Link from 'next/link'
import { useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { Segmented } from '@/components/market/segmented'
import { useQuote } from '@/components/market/live-provider'
import { DIVIDEND_TAX, feeBreakdown } from '@/lib/market/fees'
import { formatNumber, formatPercent, formatPrice } from '@/lib/market/format'

interface PositionCalculatorProps {
    ticker: string
    live: LiveSpec
    dps: number | null
}

type Residency = 'resident' | 'nonResident'

const PositionCalculator = ({ ticker, live, dps }: PositionCalculatorProps): ReactNode => {
    const quote = useQuote(live)
    const [sharesInput, setSharesInput] = useState('1000')
    const [residency, setResidency] = useState<Residency>('resident')

    const shares = Math.max(0, Math.floor(Number(sharesInput.replace(/,/g, '')) || 0))
    const consideration = shares * quote.last
    const fees = feeBreakdown(consideration)
    const totalCost = consideration + fees.total
    const sellRate = fees.rate / 100
    const breakEven = shares > 0 && sellRate < 1 ? totalCost / (shares * (1 - sellRate)) : 0
    const grossDividend = shares * (dps ?? 0)
    const tax = grossDividend * (DIVIDEND_TAX[residency] / 100)
    const netDividend = grossDividend - tax

    const rows = [
        { label: `Consideration at KES ${formatPrice(quote.last)}`, value: `KES ${formatNumber(consideration, 2)}` },
        { label: `Fees and levies (${formatNumber(fees.rate, 2)}%)`, value: `KES ${formatNumber(fees.total, 2)}` },
        { label: 'Total cost of purchase', value: `KES ${formatNumber(totalCost, 2)}`, strong: true },
        { label: 'Break-even selling price', value: `KES ${formatPrice(breakEven)}`, hint: breakEven > 0 ? `${formatPercent((breakEven / quote.last - 1) * 100, 2)} from here` : undefined },
        { label: 'Annual dividend, gross', value: `KES ${formatNumber(grossDividend, 2)}` },
        { label: `Withholding tax at ${DIVIDEND_TAX[residency]}%`, value: `KES ${formatNumber(tax, 2)}` },
        { label: 'Annual dividend, net', value: `KES ${formatNumber(netDividend, 2)}`, strong: true, hint: totalCost > 0 && netDividend > 0 ? `${formatPercent((netDividend / totalCost) * 100, 2, false)} yield on cost` : undefined }
    ]

    return (
        <div>
            <div className='flex flex-wrap items-end gap-3'>
                <label className='flex flex-col gap-1.5'>
                    <span className='text-xs text-muted-foreground'>Shares of {ticker}</span>
                    <input
                        value={sharesInput}
                        onChange={event => setSharesInput(event.target.value)}
                        inputMode='numeric'
                        className='h-9 w-36 rounded-lg border border-border bg-background px-3 text-sm tabular-nums text-foreground outline-none focus-visible:border-ring'
                    />
                </label>

                <Segmented
                    label='Tax residency'
                    options={[{ value: 'resident', label: 'Resident' }, { value: 'nonResident', label: 'Non-resident' }]}
                    value={residency}
                    onChange={setResidency}
                />
            </div>

            <dl className='mt-4 divide-y divide-border/70'>
                {rows.map(row => (
                    <div key={row.label} className='flex items-baseline justify-between gap-4 py-2 text-[13px]'>
                        <dt className='text-muted-foreground'>{row.label}</dt>
                        <dd className='text-right'>
                            <span className={row.strong ? 'font-semibold tabular-nums text-foreground' : 'tabular-nums text-foreground'}>{row.value}</span>
                            {row.hint && <span className='block text-[11px] text-muted-foreground'>{row.hint}</span>}
                        </dd>
                    </div>
                ))}
            </dl>

            <p className='mt-3 text-[11px] leading-relaxed text-muted-foreground'>
                Uses the standard levies for trades of this size and the latest declared dividend. Your broker may charge less above KES 100,000.
            </p>

            <Link href='/calculators' className='mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline'>
                Open the full calculators
                <ArrowRight aria-hidden='true' className='size-3.5' />
            </Link>
        </div>
    )
}

export default PositionCalculator
